// =============================================================================
// src/data/analytics-overlay-feed.ts — Feed, influenciadores e veículos vivos.
//
// Converte linhas do source social_listening nos contratos do Analytics:
// sl_serving_v_mentions_feed → OmarAnalyticsMention (reach/score/emocao = null e
// narrativa de IA vazia — o markup omite; F3.1/F3.2). Sentimento = IMPACTO para
// o Omar julgado pelo Jev (migration 036, 2026-09-23), só no feed paginado,
// sl_serving_v_author_window_30d → OmarAnalyticsInfluencer (já agregado no SQL,
// migration 012 — o grão diário estourava o limit 5000 da permissão),
// sl_serving_v_news_source_daily → OmarAnalyticsSite (agregado por veículo).
// =============================================================================

import type { AnalyticsFeedBuckets } from './analytics-client';
import type {
  AnalyticsActorFeedResult,
  AnalyticsAuthorWindowRow,
  AnalyticsMentionsFeedRow,
  AnalyticsNewsSourceDailyRow,
} from './analytics-live-query';
import type {
  OmarAnalyticsInfluencer,
  OmarAnalyticsMediaKind,
  OmarAnalyticsMention,
  OmarAnalyticsSentiment,
  OmarAnalyticsSite,
} from '../features/analytics/analytics-types';
import {
  analyticsDecodeHtmlEntities,
  analyticsPlatformUiLabelFor,
  analyticsPtBrCompactNumber,
  analyticsPtBrInteger,
  analyticsTimestampLabel,
} from './analytics-overlay-format';

const ANALYTICS_TOP_ROWS = 8;
const ANALYTICS_TITLE_MAX_CHARS = 90;
const ANALYTICS_MENTION_TAGS_MAX = 4;

/**
 * Feed vivo multi-bucket (decisão 2026-07-25): mescla recentes + tops por
 * engajamento/likes/views + tops por plataforma (dedupe por content_id),
 * garantindo que o filtro de canal e a ordenação "Mais populares" tenham
 * conteúdo REAL relevante — não só a cauda cronológica. Ordem exibida =
 * recente (rank default do controller); popularRank = likes/views (SQL já
 * pré-selecionou; aqui só rankeamos o conjunto mesclado).
 */
export function buildAnalyticsFeedPatch(
  feedBuckets: AnalyticsFeedBuckets,
): readonly OmarAnalyticsMention[] | undefined {
  const merged = analyticsMergeFeedBuckets(feedBuckets);
  if (merged.length === 0) return undefined;
  const byRecent = [...merged].sort(analyticsCompareByPublishedDesc);
  const byPopular = [...merged].sort(analyticsCompareByPopularityDesc);
  const popularRankById = new Map(byPopular.map((feedRow, sortedIndex) => [feedRow.content_id, sortedIndex + 1]));
  return byRecent.map((feedRow, recentIndex) =>
    /* v8 ignore next -- popularRankById foi construído destas MESMAS linhas (get nunca falha); ?? exigido pelo tipo de Map.get */
    analyticsMentionFromRow(feedRow, recentIndex + 1, popularRankById.get(feedRow.content_id) ?? recentIndex + 1));
}

/** Drill-down por personagem: recentes + top do ator (já filtrados no SQL)
 * viram cards com o MESMO merge/ranks do feed base. */
export function buildAnalyticsActorFeedItems(actorFeed: AnalyticsActorFeedResult): readonly OmarAnalyticsMention[] {
  return buildAnalyticsFeedPatch({
    recent: actorFeed.actorRecent,
    topEngagementPosts: actorFeed.actorTop,
    topLikedComments: [],
    topViewedContent: [],
    platformTops: [],
  }) ?? [];
}

/**
 * Linhas de UMA PAGINA viram cards na ordem EXATA em que o servidor mandou.
 *
 * Sem merge, sem dedupe e sem rank: a ordenacao ja e do SQL (2026-09-20). Os
 * ranks continuam sendo preenchidos porque o markup do card os carrega como
 * atributo, mas aqui eles so refletem a posicao na pagina.
 *
 * @example buildAnalyticsFeedPageItems(rows) // [card1, card2, ...]
 */
export function buildAnalyticsFeedPageItems(
  feedRows: readonly AnalyticsMentionsFeedRow[],
): readonly OmarAnalyticsMention[] {
  return feedRows.map((feedRow, index) => analyticsMentionFromRow(feedRow, index + 1, index + 1));
}

function analyticsMergeFeedBuckets(feedBuckets: AnalyticsFeedBuckets): readonly AnalyticsMentionsFeedRow[] {
  const merged: AnalyticsMentionsFeedRow[] = [];
  const seenContentIds = new Set<string>();
  const orderedBuckets = [
    feedBuckets.recent, feedBuckets.topEngagementPosts, feedBuckets.topLikedComments,
    feedBuckets.topViewedContent, feedBuckets.platformTops,
  ];
  for (const bucketRows of orderedBuckets) {
    for (const feedRow of bucketRows) {
      if (seenContentIds.has(feedRow.content_id)) continue;
      seenContentIds.add(feedRow.content_id);
      merged.push(feedRow);
    }
  }
  return merged;
}

function analyticsCompareByPublishedDesc(left: AnalyticsMentionsFeedRow, right: AnalyticsMentionsFeedRow): number {
  return (right.published_at ?? '').localeCompare(left.published_at ?? '');
}

/** "Mais populares" = likes/views: engajamento (likes+2c+3s) OU views, o maior. */
export function analyticsPopularityMetric(feedRow: AnalyticsMentionsFeedRow): number {
  return Math.max(feedRow.engagement_score, feedRow.views_count);
}

function analyticsCompareByPopularityDesc(left: AnalyticsMentionsFeedRow, right: AnalyticsMentionsFeedRow): number {
  const metricDelta = analyticsPopularityMetric(right) - analyticsPopularityMetric(left);
  if (metricDelta !== 0) return metricDelta;
  return analyticsCompareByPublishedDesc(left, right);
}

/** Rótulo do Jev (036) → vocabulário do Analytics. A tradução mora só aqui. */
const ANALYTICS_IMPACT_BY_VERDICT: Readonly<Record<string, OmarAnalyticsSentiment>> = {
  promotor: 'positive',
  neutro: 'neutral',
  detrator: 'negative',
};

/**
 * Impacto para o Omar + confiança a partir da linha do feed. Rótulo desconhecido
 * ou ausente vira null (nunca 'neutral': um default aqui viraria métrica falsa).
 *
 * @example analyticsImpactFromRow({ ...row, sentiment_verdict: 'promotor', sentiment_conf: 0.96 })
 * // { sentiment: 'positive', sentimentConfidence: 0.96 }
 */
export function analyticsImpactFromRow(
  feedRow: Pick<AnalyticsMentionsFeedRow, 'sentiment_verdict' | 'sentiment_conf'>,
): Pick<OmarAnalyticsMention, 'sentiment' | 'sentimentConfidence'> {
  const sentiment = ANALYTICS_IMPACT_BY_VERDICT[feedRow.sentiment_verdict ?? ''] ?? null;
  if (!sentiment) return { sentiment: null, sentimentConfidence: null };
  const confidence = Number(feedRow.sentiment_conf ?? Number.NaN);
  return { sentiment, sentimentConfidence: Number.isFinite(confidence) ? confidence : null };
}

function analyticsMentionFromRow(feedRow: AnalyticsMentionsFeedRow, recentRank: number, popularRank: number): OmarAnalyticsMention {
  const interactions = feedRow.likes_count + feedRow.comments_count + feedRow.shares_count;
  const isThreadComment = feedRow.content_kind === 'comment' || feedRow.content_kind === 'reply';
  return {
    mentionId: feedRow.content_id,
    author: feedRow.author_name ?? feedRow.author_handle ?? 'Autor nao identificado',
    authorHandle: feedRow.author_handle ? `@${feedRow.author_handle}` : 'sem handle',
    authorReach: feedRow.author_followers != null ? `${analyticsPtBrCompactNumber(feedRow.author_followers)} seguidores` : 'seguidores n/d',
    platform: analyticsPlatformUiLabelFor(feedRow.platform, feedRow.content_kind),
    time: feedRow.published_at ? analyticsTimestampLabel(feedRow.published_at) : 'sem data',
    ...analyticsImpactFromRow(feedRow),
    reach: null,
    score: null,
    interactions: analyticsPtBrCompactNumber(interactions),
    comments: analyticsPtBrCompactNumber(feedRow.comments_count),
    shares: analyticsPtBrCompactNumber(feedRow.shares_count),
    views: feedRow.views_count > 0 ? analyticsPtBrCompactNumber(feedRow.views_count) : null,
    emotion: null,
    title: analyticsMentionTitleFor(feedRow),
    // 2026-09-20: 6% do feed vivo chega com entidade HTML crua do RSS. O markup
    // escapa antes de renderizar (correto), entao sem decodificar aqui o leitor
    // via "Roberto Cidade &nbsp;&nbsp; Amazonas1" na tela.
    text: analyticsDecodeHtmlEntities(feedRow.text_body ?? ''),
    // Comentário sem permalink próprio "Visita" o POST-RAIZ (014).
    sourceUrlLabel: feedRow.url ?? feedRow.thread_root_url ?? '',
    contextNote: isThreadComment ? analyticsThreadContextNote(feedRow) : null,
    mediaKind: analyticsMediaKindFor(feedRow),
    recentRank,
    popularRank,
    detailLead: isThreadComment && feedRow.thread_root_text_preview
      ? `Post original: "${feedRow.thread_root_text_preview.trim()}"`
      : '',
    detailSummary: '',
    detailRisk: '',
    detailAction: '',
    detailSignals: [],
    recommendedActions: [],
    tags: [...feedRow.themes, ...feedRow.actor_names].slice(0, ANALYTICS_MENTION_TAGS_MAX),
    actors: feedRow.actor_names,
    actorSlugs: feedRow.actor_slugs,
    relations: feedRow.actor_relations,
    relevanceVerdict: feedRow.relevance_verdict,
    vipRelation: feedRow.vip_relation,
    textSubstance: feedRow.text_substance,
    rawPlatform: feedRow.platform,
    rawAuthorHandle: feedRow.author_handle,
  };
}

function analyticsMentionTitleFor(feedRow: AnalyticsMentionsFeedRow): string {
  // Comentário: o texto cru como manchete confundia ("de onde veio isso?") —
  // o título passa a NOMEAR a relação; o corpo do card mostra o comentário.
  if (feedRow.content_kind === 'comment' || feedRow.content_kind === 'reply') {
    return feedRow.thread_root_author_handle
      ? `Comentário em post de @${feedRow.thread_root_author_handle}`
      : `Comentário no ${analyticsPlatformUiLabelFor(feedRow.platform, feedRow.content_kind)}`;
  }
  const bodyText = analyticsDecodeHtmlEntities(feedRow.text_body ?? '').trim();
  if (bodyText.length === 0) {
    return `${analyticsPlatformUiLabelFor(feedRow.platform, feedRow.content_kind)} sem texto`;
  }
  if (bodyText.length <= ANALYTICS_TITLE_MAX_CHARS) return bodyText;
  return `${bodyText.slice(0, ANALYTICS_TITLE_MAX_CHARS - 1).trimEnd()}…`;
}

/** Rodapé do card do comentário: trecho do post comentado ("sobre: ..."). */
const ANALYTICS_CONTEXT_PREVIEW_CHARS = 80;
function analyticsThreadContextNote(feedRow: AnalyticsMentionsFeedRow): string | null {
  const rootPreview = (feedRow.thread_root_text_preview ?? '').trim();
  if (rootPreview.length === 0) return null;
  const clipped = rootPreview.length > ANALYTICS_CONTEXT_PREVIEW_CHARS
    ? `${rootPreview.slice(0, ANALYTICS_CONTEXT_PREVIEW_CHARS - 1).trimEnd()}…`
    : rootPreview;
  return `sobre: "${clipped}"`;
}

function analyticsMediaKindFor(feedRow: AnalyticsMentionsFeedRow): OmarAnalyticsMediaKind {
  if (feedRow.content_kind === 'news') return 'noticia';
  if (feedRow.content_kind === 'comment' || feedRow.content_kind === 'reply') return 'comentario';
  if (feedRow.content_format === 'video' || feedRow.content_format === 'reel') return 'video';
  return 'post';
}

/** Top perfis (linhas JÁ agregadas por autor×plataforma no SQL, ordenadas por
 * menções no Hasura); undefined sem linhas (mock fica). voiceShare = fatia do
 * autor sobre o TOTAL de menções da janela (denominador vem de mentionsDaily). */
export function buildAnalyticsInfluencersPatch(
  authorRows: readonly AnalyticsAuthorWindowRow[],
  windowMentionsTotal: number,
): readonly OmarAnalyticsInfluencer[] | undefined {
  if (authorRows.length === 0) return undefined;
  const shareDenominator = windowMentionsTotal > 0
    ? windowMentionsTotal
    : authorRows.reduce((sum, authorRow) => sum + authorRow.mentions, 0);
  return [...authorRows]
    .sort((left, right) => right.mentions - left.mentions)
    .slice(0, ANALYTICS_TOP_ROWS)
    .map((authorRow) => analyticsInfluencerFromWindowRow(authorRow, shareDenominator));
}

function analyticsInfluencerFromWindowRow(
  authorRow: AnalyticsAuthorWindowRow,
  shareDenominator: number,
): OmarAnalyticsInfluencer {
  const authorHandle = authorRow.author_handle ?? '(sem handle)';
  const voiceSharePct = shareDenominator > 0 ? Math.round((authorRow.mentions / shareDenominator) * 100) : 0;
  return {
    influencerId: `live-${authorHandle.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${authorRow.platform}`,
    influencerName: authorRow.author_name ?? authorHandle,
    handle: `@${authorHandle}`,
    location: 'localizacao n/d',
    platform: analyticsPlatformUiLabelFor(authorRow.platform, 'post'),
    sentiment: null,
    followers: authorRow.followers_current != null ? analyticsPtBrCompactNumber(authorRow.followers_current) : '—',
    reach: '—',
    mentions: authorRow.mentions,
    voiceShare: `${voiceSharePct}%`,
    performance: '—',
    sentimentBars: null,
    lastMention: `${analyticsPtBrInteger(authorRow.engagement_score)} de engajamento no recorte`,
    expandedMentions: [],
    // 021: coluna REAL da tabela live (substitui as demonstrativas vazias).
    engagementLabel: analyticsPtBrCompactNumber(authorRow.engagement_score),
  };
}

/** Top veículos de notícia na janela; undefined sem linhas (mock fica).
 * scopeByHandle (021): escopo editorial de config.news_outlet_scope — veículo
 * não cadastrado é nacional implícito (mesma regra do relevance scoring). */
export function buildAnalyticsSitesPatch(
  newsRows: readonly AnalyticsNewsSourceDailyRow[],
  scopeByHandle: ReadonlyMap<string, string> = new Map(),
): readonly OmarAnalyticsSite[] | undefined {
  if (newsRows.length === 0) return undefined;
  const bySource = new Map<string, { mentions: number; engagement: number }>();
  for (const newsRow of newsRows) {
    const sourceTotals = bySource.get(newsRow.source_name) ?? { mentions: 0, engagement: 0 };
    sourceTotals.mentions += newsRow.mentions;
    sourceTotals.engagement += newsRow.engagement_score;
    bySource.set(newsRow.source_name, sourceTotals);
  }
  return [...bySource.entries()]
    .sort((left, right) => right[1].mentions - left[1].mentions)
    .slice(0, ANALYTICS_TOP_ROWS)
    .map(([sourceName, sourceTotals]) => analyticsSiteFromSource(sourceName, sourceTotals, scopeByHandle));
}

function analyticsSiteFromSource(
  sourceName: string,
  sourceTotals: { readonly mentions: number; readonly engagement: number },
  scopeByHandle: ReadonlyMap<string, string>,
): OmarAnalyticsSite {
  return {
    siteId: `live-${sourceName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`,
    siteName: sourceName,
    // 'Noticias' = valor EXATO da opcao do filtro site-kind do rail (funciona live).
    siteKind: 'Noticias',
    sentiment: null,
    visits: '—',
    mentions: sourceTotals.mentions,
    reach: '—',
    performance: '—',
    sentimentBars: null,
    lastHeadline: 'Cobertura agregada do Google News (dominio do portal na proxima fase).',
    expandedMentions: [],
    engagementLabel: analyticsPtBrCompactNumber(sourceTotals.engagement),
    scopeLabel: scopeByHandle.get(sourceName.toLowerCase()) ?? 'Nacional (nao cadastrado)',
  };
}
